import { useNavigate } from "react-router-dom";
import { WaitlistForm } from "@/components/WaitlistForm";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { CalendarHeart, Timer, ShieldCheck, Globe2, ArrowRight } from "lucide-react";
import { Footer } from "@/components/Footer";

const EVENT_IMG = "https://images.unsplash.com/photo-1459749411175-04bf5292ceea?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const steps = [
  { icon: CalendarHeart, title: "Pick a real event", body: "Concerts, matches, gallery nights, mixers — curated by us.", color: "text-emerald-400" },
  { icon: Globe2, title: "Match on the plan", body: "Signal interest in going together. No endless swiping.", color: "text-pink-400" },
  { icon: Timer, title: "60-second vibe check", body: "A mandatory video call before any plans are final.", color: "text-amber-400" },
  { icon: ShieldCheck, title: "Both say yes → meet", body: "Chat unlocks only when you both feel it. Report & block always on.", color: "text-emerald-400" },
];

export default function Landing() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen">
      <header className="max-w-6xl mx-auto px-5 h-20 flex items-center justify-between">
        <Logo size="text-3xl" />
        <Button data-testid="landing-signin-top" onClick={() => navigate("/login")}
          className="btn-gold rounded-full px-6 h-10">Sign in</Button>
      </header>

      <section className="max-w-6xl mx-auto px-5 pt-10 lg:pt-16 grid lg:grid-cols-2 gap-12 items-center">
        <div className="fade-up">
          <span className="chip inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-widest">
            Event-driven dating
          </span>
          <h1 className="font-display font-black tracking-tight text-5xl sm:text-6xl lg:text-7xl mt-6 leading-[0.95]">
            Date around the <span className="gold-text">moment</span>, not the <span className="pink-text">profile</span>.
          </h1>
          <p className="text-base sm:text-lg text-emerald-50/70 mt-6 max-w-xl leading-relaxed">
            MoYo Stars Date connects you over real events — a concert, a match, a gallery night. Match on the plan,
            pass a <span className="text-amber-300 font-semibold">60-second video vibe check</span>, then go together.
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-8">
            <Button data-testid="landing-get-started" onClick={() => navigate("/login")}
              className="btn-gold rounded-full h-12 px-8 text-base">
              Get started <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button data-testid="landing-signin" variant="ghost" onClick={() => navigate("/login")}
              className="rounded-full h-12 px-6 text-emerald-100/80 hover:text-white hover:bg-white/5">
              I have an account
            </Button>
          </div>
          <div className="mt-5 max-w-md"><WaitlistForm source="landing" compact /></div>
          <div className="flex items-center gap-6 mt-10 text-sm text-emerald-100/50">
            <span>Now live in Clapham, London</span><span className="h-1 w-1 rounded-full bg-amber-400/60" />
            <span>Live events</span><span className="h-1 w-1 rounded-full bg-amber-400/60" />
            <span>Vibe-first</span>
          </div>
        </div>

        <div className="relative fade-up" style={{ animationDelay: ".1s" }}>
          <div className="relative rounded-[28px] overflow-hidden border border-amber-500/20 shadow-2xl">
            <img src={EVENT_IMG} alt="event" className="h-[460px] w-full object-cover" />
            <div className="absolute inset-0" style={{ background: "linear-gradient(to top, #0B130E, rgba(11,19,14,.2) 55%, transparent)" }} />
            <div className="absolute bottom-5 left-5 right-5 glass rounded-2xl p-4 flex items-center gap-4">
              <div className="relative h-16 w-16 shrink-0">
                <svg viewBox="0 0 64 64" className="h-16 w-16 -rotate-90">
                  <circle cx="32" cy="32" r="28" fill="none" stroke="rgba(245,158,11,0.15)" strokeWidth="5" />
                  <circle cx="32" cy="32" r="28" fill="none" stroke="#F59E0B" strokeWidth="5" strokeDasharray="176" strokeDashoffset="52" strokeLinecap="round" />
                </svg>
                <span className="absolute inset-0 flex items-center justify-center font-mono-doek text-amber-400 font-bold">60</span>
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest text-amber-400/80">The gatekeeper</p>
                <p className="font-display font-bold text-lg">60-second vibe check</p>
                <p className="text-xs text-emerald-100/60">No small talk marathons. Just a real first spark.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-5 py-20">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {steps.map((s, i) => (
            <div key={s.title} className="glass rounded-2xl p-6 fade-up hover:border-amber-500/40 transition-colors" style={{ animationDelay: `${i * 0.06}s` }}>
              <div className="h-11 w-11 rounded-xl bg-white/5 flex items-center justify-center mb-4">
                <s.icon className={`h-5 w-5 ${s.color}`} />
              </div>
              <h3 className="font-display font-bold text-lg">{s.title}</h3>
              <p className="text-sm text-emerald-100/60 mt-2 leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </section>
      <Footer />
    </div>
  );
}
